/**
 * /mcp/authorize?req=<id> — OAuth consent for AI clients (Claude, etc.)
 * connecting to the MyLeadSyncer MCP server.
 *
 * Always acts as the signed-in user's own account: both calls go out without
 * the X-MLS-Workspace header, whatever workspace this tab is switched into.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SignIn, useAuth as useClerkAuth, useUser } from '@clerk/react';
import { Bot, ArrowUpRight, Check, X, AlertTriangle, ShieldCheck } from 'lucide-react';
import { mcpAuthorizeApi } from '../lib/api';
import type { McpAuthorizeRequest } from '../lib/api';
import { Button, Spinner } from '../components/ui';
import { AuthShell } from './Login';

/** Schemes that would run code or read local data instead of returning to the client. */
const BLOCKED_PROTOCOLS = new Set(['javascript:', 'data:', 'vbscript:', 'file:', 'blob:']);

/**
 * Only navigate to a parseable URL with a safe scheme. http(s) on any host is
 * allowed (MCP clients often use localhost callbacks), as are custom app
 * schemes such as cursor:// or vscode:// used by native clients.
 */
function safeRedirect(redirectTo: string): string | null {
  try {
    const url = new URL(redirectTo);
    if (BLOCKED_PROTOCOLS.has(url.protocol.toLowerCase())) return null;
    return url.href;
  } catch {
    return null;
  }
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; request: McpAuthorizeRequest }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string };

export default function McpAuthorize() {
  const [params] = useSearchParams();
  const requestId = params.get('req') ?? '';
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [deciding, setDeciding] = useState<'approve' | 'deny' | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!requestId) { setState({ kind: 'invalid' }); return; }
    let cancelled = false;
    setState({ kind: 'loading' });
    mcpAuthorizeApi.get(requestId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) setState({ kind: 'ready', request: res.data });
      else if (res.status === 404 || res.status === 410) setState({ kind: 'invalid' });
      else setState({ kind: 'error', message: res.message || 'Could not load this request.' });
    });
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, requestId]);

  const decide = async (approve: boolean) => {
    setDeciding(approve ? 'approve' : 'deny');
    setDecideError(null);
    const res = await mcpAuthorizeApi.decide(requestId, approve);
    if (res.success && res.data?.redirectTo) {
      const target = safeRedirect(res.data.redirectTo);
      if (target) {
        window.location.assign(target);
        return;
      }
      setDecideError('The app returned an unsafe redirect address, so you were not sent back. Close this tab and try connecting again.');
      setDeciding(null);
      return;
    }
    if (res.status === 404 || res.status === 410) {
      setState({ kind: 'invalid' });
    } else {
      setDecideError(res.message || 'Something went wrong. Please try again.');
    }
    setDeciding(null);
  };

  if (!isLoaded) {
    return <AuthShell><Spinner /></AuthShell>;
  }

  if (!isSignedIn) {
    const back = window.location.href;
    return (
      <AuthShell>
        <div className="w-full flex flex-col items-center">
          <p className="mb-4 text-center text-[13.5px] font-medium text-ink-2">
            Sign in to connect an AI assistant to your My Lead Syncer account.
          </p>
          <SignIn routing="hash" forceRedirectUrl={back} signUpForceRedirectUrl={back} />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="w-full bg-surface border border-line rounded-[18px] shadow-card p-7">
        {state.kind === 'loading' && (
          <div className="flex justify-center py-10"><Spinner /></div>
        )}

        {state.kind === 'invalid' && (
          <div className="text-center">
            <div className="mx-auto w-11 h-11 rounded-[12px] bg-warning-soft text-warning flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display font-bold text-[20px] text-ink tracking-tight">This request has expired</h1>
            <p className="mt-2 text-[13.5px] font-medium text-ink-2">
              The connection link is invalid or too old. Go back to your AI assistant and start connecting again.
            </p>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="text-center">
            <div className="mx-auto w-11 h-11 rounded-[12px] bg-danger-soft text-danger flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display font-bold text-[20px] text-ink tracking-tight">Something went wrong</h1>
            <p className="mt-2 text-[13.5px] font-medium text-ink-2">{state.message}</p>
            <Button variant="ghost" className="mt-5 mx-auto" onClick={() => window.location.reload()}>Try again</Button>
          </div>
        )}

        {state.kind === 'ready' && (
          <>
            <div className="w-11 h-11 rounded-[12px] bg-accent-soft text-accent-ink flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display font-bold text-[21px] leading-snug text-ink tracking-tight">
              <span className="break-words">{state.request.clientName}</span>{' '}
              <span className="font-sans font-semibold text-[12px] text-ink-3 align-middle">(unverified name)</span>
            </h1>
            <p className="mt-2 text-[14px] font-medium text-ink-2">
              wants to access your My Lead Syncer forms, quizzes, buttons and popups.
            </p>

            <div className="mt-5 p-4 rounded-[12px] bg-warning-soft border border-warning/30">
              <div className="flex items-start gap-2.5">
                <ArrowUpRight className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="m-0 text-[14px] font-bold text-ink">
                    You'll be sent back to <span className="font-mono break-all">{state.request.redirectHost}</span>
                  </p>
                  <p className="m-0 mt-1 text-[12.5px] font-medium text-ink-2">
                    Only approve if you started this connection from that app.
                  </p>
                </div>
              </div>
            </div>

            {state.request.scopes.length > 0 && (
              <ul className="mt-4 space-y-1.5">
                {state.request.scopes.map((scope) => (
                  <li key={scope} className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-2">
                    <ShieldCheck className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="font-mono">{scope}</span>
                  </li>
                ))}
              </ul>
            )}

            {clerkUser?.primaryEmailAddress && (
              <p className="mt-5 text-[12.5px] font-medium text-ink-3">
                Signed in as <span className="font-semibold text-ink-2">{clerkUser.primaryEmailAddress.emailAddress}</span>
              </p>
            )}

            {decideError && (
              <p className="mt-3 text-[13px] font-semibold text-danger">{decideError}</p>
            )}

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Button variant="ghost" icon={X} disabled={deciding !== null} onClick={() => void decide(false)} className="justify-center">
                {deciding === 'deny' ? 'Denying…' : 'Deny'}
              </Button>
              <Button icon={Check} disabled={deciding !== null} onClick={() => void decide(true)} className="justify-center">
                {deciding === 'approve' ? 'Approving…' : 'Approve'}
              </Button>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
